import * as axios from 'axios';
import Axios, { AxiosError, AxiosResponse } from 'axios';
import exif2css from 'exif2css';
import { FormApi } from 'final-form';
import * as React from 'react';
import AxiosHelper from 'services/AxiosHelper';
import GarbageCanSVG from 'shared/components/svg/GarbageCanSVG';
import SparkMD5 from 'spark-md5';
import EXIF from './lib/exif-js/exif';
import { FileUploadResponse, FormValuesWithFileUploadField } from './FileUpload';
import DisplayBytes from './DisplayBytes';
import { Localizer } from 'services/Localizer';
import { useState } from 'react';

export type FileUploadInfoProps<T extends FormValuesWithFileUploadField> = {
	formApi: FormApi<T>;
	file: any;
	onRemoveFile: () => void;
	onUploadError: (error: string) => void;
};

export interface FileUploadInfoState {
	uploadProgress: number;
	uploadComplete: boolean;
	uploadError: boolean;
	previewUrl: string;
}

/**
 * Renders all fields needed for uploading a file, and handles file uploads and deletes.
 */
function FileUploadInfo<T extends FormValuesWithFileUploadField>({
	file,
	formApi,
	onRemoveFile,
	onUploadError,
}: FileUploadInfoProps<T>) {
	const [state, setState] = useState({
		uploadProgress: 0,
		uploadComplete: false,
		uploadError: false,
		previewUrl: URL.createObjectURL(file),
	} as FileUploadInfoState);

	React.useEffect(() => {
		const acceptedFile = file;

		setState({
			uploadProgress: 0,
			uploadComplete: false,
			uploadError: false,
			previewUrl: URL.createObjectURL(file),
		} as FileUploadInfoState);

		// START SPARKMD5
		// Calculates the md5 hash for the file to compare against the hash generated by the server
		// This ensures the file has been uploaded correctly and also allows for checking of duplicate files
		const blobSlice = File.prototype.slice;
		const chunkSize = 2097152; // Read in chunks of 2MB
		const chunks = Math.ceil(acceptedFile.size / chunkSize);
		let currentChunk = 0;
		const spark = new SparkMD5.ArrayBuffer();

		const fileReader = new FileReader();

		fileReader.onload = (e) => {
			if (e.target) {
				const fileReaderTarget = e.target as FileReader;
				spark.append(fileReaderTarget.result); // Append array buffer
				currentChunk++;

				if (currentChunk < chunks) {
					loadNext();
				} else {
					const clientSideHash = spark.end();

					const formData = new FormData();
					formData.append('file', acceptedFile, acceptedFile.name);

					const url = `ajax/file-upload/`;

					AxiosHelper.post(url, formData, {
						timeout: 1000000, // 1000 seconds
						headers: { 'Content-type': 'multipart/form-data' },
						onUploadProgress(progressEvent) {
							const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							updateProgress(percentCompleted);
						},
						cancelToken: new axios.default.CancelToken(() => {}),
					})
						.then(
							(x) =>
								new Promise((resolve) => {
									updateProgress(100);
									setTimeout(() => resolve(x), 500); // half a second seems to produce best visual effect
								}),
						) // delay so user can see the 100% progress
						.then((resp: AxiosResponse<any>) => {
							const fileInfo = resp.data as FileUploadResponse;
							if (fileInfo.checksum === clientSideHash) {
								setState({ ...state, uploadComplete: true });
								formApi.change('uploadedFileInfo', fileInfo);
								file.uploadGuid = fileInfo.guid;
							} else {
								// reset the upload form, and show the error message
								onUploadError(Localizer('ChecksumValidationError'));
								setState({ ...state, uploadComplete: false, uploadError: true });
							}
						})
						.catch((error: AxiosError) => {
							if (Axios.isCancel(error)) {
								// if the component was unmounted, there's nothing to do
								return;
							}

							let consoleError = error.message;

							// try to parse message from the html to log a more specific error to the console
							if (error.response && error.response.status >= 500) {
								const errorResponse = new DOMParser().parseFromString(error.response.data, 'text/html');
								const errorMessage = errorResponse.getElementsByClassName('stackerror');
								if (errorMessage && errorMessage.length > 0) {
									consoleError = errorMessage[0].innerHTML;
								}
							}
							console.error(consoleError);

							// reset the upload form, and show the error message
							onUploadError(Localizer('FileServerSideValidation'));
							setState({ ...state, uploadComplete: false, uploadError: true });
						});
				}
			}
		};

		fileReader.onerror = (e) => {
			console.error('File could not be read!');
			const fileReaderTarget = e.target as FileReader;
			fileReaderTarget.abort();
			if (fileReaderTarget) {
				console.debug(' -> Code ' + fileReaderTarget.error);
			}
		};

		function loadNext() {
			const start = currentChunk * chunkSize;
			const end = start + chunkSize >= acceptedFile.size ? acceptedFile.size : start + chunkSize;

			fileReader.readAsArrayBuffer(blobSlice.call(acceptedFile, start, end));
		}

		loadNext();
		// END SPARKMD5
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [file]);

	const updateProgress = (percentCompleted) => {
		setState({ ...state, uploadProgress: percentCompleted });
	};

	/**
	 * Rotates the preview image using CSS based on the EXIF Orientation value.
	 */
	const onPreviewImageLoaded = (event) => {
		const imageElement: HTMLImageElement = event.target;

		// rotate the image based on exif data, if there is any
		EXIF.getData(imageElement, () => {
			const orientation = EXIF.getTag(imageElement, 'Orientation');
			if (orientation) {
				const css = exif2css(orientation);
				if (css.transform) {
					imageElement.style.transform = css.transform;
				}
				if (css['transform-origin']) {
					imageElement.style.transformOrigin = css['transform-origin'];
				}
			}
		});
	};

	/**
	 * Triggers the onRemoveFile property that was passed into the component to remove the file from the form
	 */
	const removeButtonClicked = async () => {
		// revoke preview blob url, avoid memory leak
		URL.revokeObjectURL(file.previewUrl);

		formApi.change('uploadedFileInfo', null);
		onRemoveFile();

		// delete from temporary upload
		const url = `/ajax/file-upload/` + file.uploadGuid;
		const requestToken = Axios.CancelToken.source();
		await AxiosHelper.delete(url, {
			timeout: 1000000,
			cancelToken: requestToken.token,
		});
	};

	const renderThumbnail = (file, onPreviewImageLoaded, onRemoveButtonClicked) => {
		return (
			<div className="border justify-content-center text-center mb-3">
				<div className="m-5">
					{file.type.toLowerCase().indexOf('image') > -1 ? (
						<img
							data-testid="preview-image"
							alt={file.name}
							src={state.previewUrl}
							className="file__upload-image"
							onLoad={onPreviewImageLoaded}
						/>
					) : null}
					<span className="form-label-text" style={{ fontWeight: 500 }}>
						{file.name}
						<br />
						{Localizer('FileSizeLabel')} <DisplayBytes bytes={file.size} />
					</span>
				</div>
				<button color="danger" className="btn-medium mt-3" onClick={onRemoveButtonClicked}>
					<div className="btn-icon-text-container">
						<span className="btn-icon-bg">
							<GarbageCanSVG />
						</span>
						<span className="btn-icon-text">{Localizer('RemoveFile')}</span>
					</div>
				</button>
			</div>
		);
	};

	const renderUploadProgress = (state) => {
		<div className="" style={{ border: 'none', boxShadow: 'none' }}>
			<strong>{Localizer('UploadProgress', state.uploadProgress.toString())}</strong>
		</div>;
	};

	return (
		<>
			{renderThumbnail(file, onPreviewImageLoaded, removeButtonClicked)}
			{!state.uploadComplete && !state.uploadError ? renderUploadProgress(state) : null}
		</>
	);
}

export default FileUploadInfo;
